import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withNoXsrfProtection } from '@angular/common/http';
import localeEsAr from '@angular/common/locales/es-AR';
import {
	ApplicationConfig,
	importProvidersFrom,
	inject,
	LOCALE_ID,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { ACTIVATION_FLOW_ROUTING } from '@webapp/activations';
import { authInterceptor } from '@webapp/auth/interceptors/auth.interceptor';
import { credentialsInterceptor } from '@webapp/auth/interceptors/credentials.interceptor';
import { xsrfInterceptor } from '@webapp/auth/interceptors/xsrf.interceptor';
import { silentRefresh } from '@webapp/auth/bootstrap/silent-refresh.initializer';
import { channelInterceptor } from '@webapp/core/helpers/channel.nterceptor';
import { HttpErrorInterceptor, UNAUTHORIZED_REDIRECT_URL } from '@webapp/core/helpers/error.interceptor';
import { provideNgxMask } from 'ngx-mask';
import { routes } from './app.routes';
import { AuthService } from '@webapp/auth/services/auth.service';
import { API_CONFIG, APP_VERSION, CHANNEL, CHANNEL_QUOTE_ENUM, RECAPTCHA_SITE_KEY } from '@webapp/core/config';
import { QUOTER_ROUTE_PATH } from '@webapp/core';
import { provideWebPlatform } from '@webapp/platform';
import { provideRiskPlugins } from '@webapp/risk-core';
import { ALL_RISK_PLUGINS, DEFAULT_PLUGIN } from '@webapp/risks/plugins/all-risk-plugins';
import { provideWebRiskUi } from '@webapp/risks/web';
import { provideQuoterRiskUi } from '@webapp/quoter/config/provide-quoter-risk-ui';
import { environment } from '../environments/environment';

registerLocaleData(localeEsAr, 'es-AR');

export const appConfig: ApplicationConfig = {
	providers: [
		provideNgxMask(),
		provideHttpClient(
			withInterceptors([
				channelInterceptor,
				// credentialsInterceptor MUST run before authInterceptor (and every other
				// interceptor that hits the network): it sets withCredentials so cookies
				// (access-token, refresh-token, XSRF-TOKEN) are sent on EVERY request,
				// including the login/refresh/logout calls authInterceptor itself triggers.
				// Also covers this app's anonymous visitors: the silent-refresh call made
				// during bootstrap (see provideAppInitializer below) must carry credentials
				// too, even though no session cookie exists yet.
				credentialsInterceptor,
				// authInterceptor MUST run before HttpErrorInterceptor: it is outer on the
				// request path so HttpErrorInterceptor (inner on the response path) still
				// catches the 401 of any request left unauthenticated after a failed refresh.
				authInterceptor,
				// xsrfInterceptor only matches the 2 session-revoke routes, so its position
				// relative to auth/error handling is low-risk; it sits after authInterceptor
				// (so an already-attached Authorization header is preserved) and before
				// HttpErrorInterceptor (so a synthetic csrfMissing rejection reaches the
				// component via the same error path as a real backend 403).
				xsrfInterceptor,
				HttpErrorInterceptor,
			]),
			withNoXsrfProtection()
		),
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(
			routes,
			withInMemoryScrolling({
				scrollPositionRestoration: 'top',
			}),
			withRouterConfig({
				urlUpdateStrategy: 'deferred',
			})
		),
		provideAnimations(),
		provideWebPlatform(),
		provideRiskPlugins(ALL_RISK_PLUGINS, DEFAULT_PLUGIN),
		provideWebRiskUi(),
		provideQuoterRiskUi(),
		importProvidersFrom([
			JwtModule.forRoot({
				config: {
					// The access token now lives only in AuthService's in-memory signal
					// (cookies carry the session across reloads). JwtModule is kept solely
					// for JwtHelperService DI — every decode/expiry call now passes an
					// explicit token, so this getter is intentionally never read.
					tokenGetter: () => null,
				},
			}),
		]),
		// public-quoter's visitors are frequently anonymous (no refresh-token cookie
		// at all). silentRefresh() bounds this call with a client-side timeout so a
		// hung/slow backend can never block first paint indefinitely — see
		// silent-refresh.initializer.ts for the cold-start rationale (T15).
		provideAppInitializer(() => silentRefresh(inject(AuthService))),
		{ provide: LOCALE_ID, useValue: 'es-AR' },
		{ provide: API_CONFIG, useValue: environment.apiUrl },
		{ provide: CHANNEL, useValue: CHANNEL_QUOTE_ENUM.DIRECT },
		{ provide: UNAUTHORIZED_REDIRECT_URL, useValue: QUOTER_ROUTE_PATH },
		{ provide: APP_VERSION, useValue: environment.version },
		{ provide: RECAPTCHA_SITE_KEY, useValue: environment.recaptchaSiteKey },
		{ provide: ACTIVATION_FLOW_ROUTING, useValue: { entry: ['/activacion'], exit: ['/mis-polizas'] } },
	],
};
